// External Imports
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import Chart from 'react-apexcharts';

import Accordion from 'react-bootstrap/Accordion';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import Table from 'react-bootstrap/Table';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';

import Moment from 'react-moment';

import Alerts from './Alerts';
import StarlinkTelemetry from './StarlinkTelemetry';

//Hooks
import { useAuth } from 'react-oidc-context';
import useToken from '../utils/useToken';
import useBearerToken from '../utils/useBearerToken';
import ForbiddenError from '../utils/error';
import { useTimezoneContext } from '../utils/TimezoneContext';
import { formatInTimezone, getPartsInTimezone, localMidnightToUTC } from '../utils/tzFormat';

import {
  fetchStarlinkTerminal,
  fetchStarlinkTerminalUtilisation,
  fetchStarlinkTerminalTelemetry,
  fetchStarlinkTerminalTopup,
} from '../utils/starlink';

const computeDateRange = (timeframe, offset, timezone) => {
  const { year, month, day } = getPartsInTimezone(new Date(), timezone);

  if (timeframe === 'weekly') {
    const proxy = new Date(Date.UTC(year, month - 1, day));
    const dayOfWeek = proxy.getUTCDay() || 7; // Mon=1 … Sun=7
    const monday = new Date(Date.UTC(year, month - 1, day - dayOfWeek + 1 - 7 * offset));
    const nextMonday = new Date(
      Date.UTC(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate() + 7),
    );
    return {
      from_dt: new Date(
        localMidnightToUTC(
          monday.getUTCFullYear(),
          monday.getUTCMonth() + 1,
          monday.getUTCDate(),
          timezone,
        ),
      ).toISOString(),
      to_dt: new Date(
        localMidnightToUTC(
          nextMonday.getUTCFullYear(),
          nextMonday.getUTCMonth() + 1,
          nextMonday.getUTCDate(),
          timezone,
        ),
      ).toISOString(),
    };
  }
  if (timeframe === 'monthly') {
    const from = new Date(Date.UTC(year, month - 1 - offset, 1));
    const to = new Date(Date.UTC(year, month - offset, 1));
    return {
      from_dt: new Date(
        localMidnightToUTC(from.getUTCFullYear(), from.getUTCMonth() + 1, 1, timezone),
      ).toISOString(),
      to_dt: new Date(
        localMidnightToUTC(to.getUTCFullYear(), to.getUTCMonth() + 1, 1, timezone),
      ).toISOString(),
    };
  }
  const targetYear = year - offset;
  return {
    from_dt: new Date(localMidnightToUTC(targetYear, 1, 1, timezone)).toISOString(),
    to_dt: new Date(localMidnightToUTC(targetYear + 1, 1, 1, timezone)).toISOString(),
  };
};

const periodLabel = (timeframe, offset, timezone) => {
  const { year, month, day } = getPartsInTimezone(new Date(), timezone);
  if (timeframe === 'weekly') {
    if (offset === 0) return 'This Week';
    // Use a UTC proxy date so day-of-week arithmetic is timezone-unambiguous
    const proxy = new Date(Date.UTC(year, month - 1, day));
    const dayOfWeek = proxy.getUTCDay() || 7; // Mon=1 … Sun=7
    const from = new Date(Date.UTC(year, month - 1, day - dayOfWeek + 1 - 7 * offset));
    const to = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate() + 6));
    const fmt = { day: '2-digit', month: 'short' };
    return (
      formatInTimezone(from, 'UTC', fmt) +
      ' – ' +
      formatInTimezone(to, 'UTC', { ...fmt, year: 'numeric' })
    );
  }
  if (timeframe === 'monthly') {
    if (offset === 0) return 'This Month';
    const d = new Date(Date.UTC(year, month - 1 - offset, 1));
    return formatInTimezone(d, 'UTC', { month: 'long', year: 'numeric' });
  }
  if (offset === 0) return 'This Year';
  return String(year - offset);
};

export default function Starlink() {
  const auth = useAuth();
  const { token, isValidToken } = useToken();
  const { isLoggedIn, getToken } = useBearerToken();
  const params = useParams();
  const { timezone } = useTimezoneContext();

  // Local Data
  const [customerId] = useState(params.customerId);
  const [terminalId] = useState(params.terminalId);

  const [terminalLoading, setTerminalLoading] = useState(true);
  const [utilisationLoading, setUtilisationLoading] = useState(true);
  const [telemetryLoading, setTelemetryLoading] = useState(true);
  const [topupLoading, setTopupLoading] = useState(true);

  //const [daily_usage, setDailyChart] = useState({});

  const [terminal, setTerminal] = useState({});
  const [telemetry, setTerminalTelemetry] = useState({});
  const [topup, setTopup] = useState(null);

  const [utilisation, setUtilisationChart] = useState({});
  const [history, setHistoryCharts] = useState({});

  const [errors, setErrors] = useState([]);
  const [yearlyResolution, setYearlyResolution] = useState('monthly');
  const [offsets, setOffsets] = useState({ weekly: 0, monthly: 0, yearly: 0 });
  const [hasEarlier, setHasEarlier] = useState({ weekly: true, monthly: true, yearly: true });
  const [historyNavLoading, setHistoryNavLoading] = useState({
    weekly: false,
    monthly: false,
    yearly: false,
  });

  const formatGB = (gb) => {
    if (gb < 1) {
      return Math.round(gb * 1000) + ' MB';
    }
    return Math.round(gb * 10) / 10 + ' GB';
  };

  const formatHour = (val) => {
    const d = new Date(val);
    const { hour } = getPartsInTimezone(d, timezone);
    return (
      formatInTimezone(d, timezone, { day: '2-digit', month: 'short', year: 'numeric' }) +
      ' ' +
      String(hour).padStart(2, '0') +
      ':00'
    );
  };

  const formatDay = (val) =>
    formatInTimezone(new Date(val), timezone, { day: '2-digit', month: 'short', year: 'numeric' });

  const formatWeek = (val) => {
    const { year, month, day } = getPartsInTimezone(new Date(val), timezone);
    const tmp = new Date(Date.UTC(year, month - 1, day));
    tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    const week = Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
    return `Week ${week} ${year}`;
  };

  const formatMonth = (val) =>
    formatInTimezone(new Date(val), timezone, { month: 'short', year: 'numeric' });

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
  const buildUtilisationChart = (title, data, formatter, label) => {
    return {
      isEmpty: Object.keys(data.consumedAmountGB).length === 0,
      options: {
        chart: {
          id: title,
        },
        title: {
          text:
            label ??
            { weekly: 'Last 1 Week', monthly: 'Last 1 Month', yearly: 'Last 1 Year' }[title] ??
            capitalize(title),
        },
        xaxis: {
          labels: {
            formatter: formatter,
          },
          categories: Object.keys(data.consumedAmountGB).map((x) => Number(x) * 1000),
        },
        dataLabels: {
          enabled: false,
        },
        yaxis: {
          labels: {
            formatter: formatGB,
          },
        },
      },
      series: [
        {
          name: 'utilisation',
          data: Object.values(data.consumedAmountGB).map((v) => Math.max(0, v)),
        },
      ],
    };
  };

  const getStarlinkTerminal = async () => {
    if (!isLoggedIn(auth, isValidToken)) {
      window.location.reload(false);
    }

    const bearerToken = getToken(auth, isValidToken, token);
    if (bearerToken == '') {
      return;
    }

    fetchStarlinkTerminal(terminalId, customerId, bearerToken)
      .then((data) => {
        setTerminal(data);
        setTerminalLoading(false);
      })
      .catch((error) => {
        if (error instanceof ForbiddenError) {
          setErrors([
            ...errors,
            { id: 'service_error', message: '', severity: 'danger', access_denied: true },
          ]);
        } else {
          setErrors([
            ...errors,
            {
              id: 'service_error',
              message: 'Could not retrieve Starlink terminal! ' + error,
              severity: 'danger',
              access_denied: false,
            },
          ]);
        }
      });
  };

  const getStarlinkTerminalUtilisation = async () => {
    if (!isLoggedIn(auth, isValidToken)) {
      window.location.reload(false);
    }

    const bearerToken = getToken(auth, isValidToken, token);
    if (bearerToken == '') {
      return;
    }

    fetchStarlinkTerminalUtilisation(terminalId, customerId, bearerToken)
      .then((data) => {
        //setTerminalUtilisation(data);

        const emptyHistory = { consumedAmountGB: {} };
        setHistoryCharts({
          weekly: buildUtilisationChart(
            'weekly',
            data.history.weekly ?? emptyHistory,
            formatHour,
            periodLabel('weekly', 0, timezone),
          ),
          monthly: buildUtilisationChart(
            'monthly',
            data.history.monthly ?? emptyHistory,
            formatDay,
            periodLabel('monthly', 0, timezone),
          ),
          yearly_weekly: buildUtilisationChart(
            'yearly',
            data.history.yearly_weekly ?? emptyHistory,
            formatWeek,
            periodLabel('yearly', 0, timezone),
          ),
          yearly_monthly: buildUtilisationChart(
            'yearly',
            data.history.yearly_monthly ?? emptyHistory,
            formatMonth,
            periodLabel('yearly', 0, timezone),
          ),
        });

        var contracted_volume = 0;
        if (terminal.contracted_volume != null) {
          contracted_volume = terminal.contracted_volume;
        } else {
          contracted_volume = data.subscription_available;
        }
        var available_gb = contracted_volume - data.subscription_consumed;
        // Consumption can exceed contracted_volume (e.g. after a top-up) - clamp
        // the pie slice instead of feeding it a negative value, and surface the
        // overage separately so it isn't silently hidden.
        var over_contract_gb = available_gb < 0 ? -available_gb : 0;

        setUtilisationChart({
          options: {
            chart: {
              id: 'utilisation',
              toolbar: {
                show: true,
              },
            },
            title: {
              text: 'Current Utilisation',
            },
            labels: ['Consumed', 'Available'],
            dataLabels: {
              enabled: false,
            },
            yaxis: {
              labels: {
                formatter: formatGB,
              },
            },
          },
          series: [data.subscription_consumed, Math.max(0, available_gb)],
          overContractGB: over_contract_gb,
          // data.plan_exhausted is only ever set once the backend is reading v2.
          planExhausted: data.plan_exhausted === true,
        });

        setUtilisationLoading(false);
        navigateHistory('weekly', 0);
        navigateHistory('monthly', 0);
        navigateHistory('yearly', 0);
      })
      .catch((error) => {
        if (error instanceof ForbiddenError) {
          setErrors([
            ...errors,
            { id: 'utilisation_error', message: '', severity: 'danger', access_denied: true },
          ]);
        } else {
          setErrors([
            ...errors,
            {
              id: 'utilisation_error',
              message: 'Could not retrieve starlink terminal utilisation! ' + error,
              severity: 'danger',
              access_denied: false,
            },
          ]);
        }
      });
  };

  const navigateHistory = (timeframe, newOffset) => {
    const bearerToken = getToken(auth, isValidToken, token);
    if (bearerToken === '') return;

    setHistoryNavLoading((prev) => ({ ...prev, [timeframe]: true }));
    const { from_dt, to_dt } = computeDateRange(timeframe, newOffset, timezone);

    fetchStarlinkTerminalUtilisation(terminalId, customerId, bearerToken, timeframe, from_dt, to_dt)
      .then((data) => {
        setHistoryCharts((prev) => {
          const updates = {};
          const emptyHistory = { consumedAmountGB: {} };
          if (timeframe === 'weekly') {
            updates.weekly = buildUtilisationChart(
              'weekly',
              data.history.weekly ?? emptyHistory,
              formatHour,
              periodLabel('weekly', newOffset, timezone),
            );
          } else if (timeframe === 'monthly') {
            updates.monthly = buildUtilisationChart(
              'monthly',
              data.history.monthly ?? emptyHistory,
              formatDay,
              periodLabel('monthly', newOffset, timezone),
            );
          } else if (timeframe === 'yearly') {
            updates.yearly_weekly = buildUtilisationChart(
              'yearly',
              data.history.yearly_weekly ?? emptyHistory,
              formatWeek,
              periodLabel('yearly', newOffset, timezone),
            );
            updates.yearly_monthly = buildUtilisationChart(
              'yearly',
              data.history.yearly_monthly ?? emptyHistory,
              formatMonth,
              periodLabel('yearly', newOffset, timezone),
            );
          }
          return { ...prev, ...updates };
        });
        setOffsets((prev) => ({ ...prev, [timeframe]: newOffset }));
        if (timeframe === 'weekly')
          setHasEarlier((prev) => ({ ...prev, weekly: data.history.weekly_has_earlier !== false }));
        if (timeframe === 'monthly')
          setHasEarlier((prev) => ({
            ...prev,
            monthly: data.history.monthly_has_earlier !== false,
          }));
        if (timeframe === 'yearly')
          setHasEarlier((prev) => ({ ...prev, yearly: data.history.yearly_has_earlier !== false }));
        setHistoryNavLoading((prev) => ({ ...prev, [timeframe]: false }));
      })
      .catch((error) => {
        if (error instanceof ForbiddenError) {
          setErrors((prev) => [
            ...prev,
            { id: 'utilisation_nav_error', message: '', severity: 'danger', access_denied: true },
          ]);
        } else {
          setErrors((prev) => [
            ...prev,
            {
              id: 'utilisation_nav_error',
              message: 'Could not retrieve utilisation data! ' + error,
              severity: 'danger',
              access_denied: false,
            },
          ]);
        }
        setHistoryNavLoading((prev) => ({ ...prev, [timeframe]: false }));
      });
  };

  const getStarlinkTerminalTelemetry = async () => {
    if (!isLoggedIn(auth, isValidToken)) {
      window.location.reload(false);
    }

    const bearerToken = getToken(auth, isValidToken, token);
    if (bearerToken == '') {
      return;
    }

    fetchStarlinkTerminalTelemetry(terminalId, customerId, bearerToken)
      .then((data) => {
        setTerminalTelemetry(data.telemetry);
        setTelemetryLoading(false);
      })
      .catch((error) => {
        if (error instanceof ForbiddenError) {
          setErrors([
            ...errors,
            { id: 'telemetry_error', message: '', severity: 'danger', access_denied: true },
          ]);
        } else {
          setErrors([
            ...errors,
            {
              id: 'telemetry_error',
              message: 'Could not retrieve starlink terminal telemetry! ' + error,
              severity: 'danger',
              access_denied: false,
            },
          ]);
        }
      });
  };

  useEffect(() => {
    getStarlinkTerminal();
  }, [terminalId]);

  const getStarlinkTerminalTopup = async () => {
    const bearerToken = getToken(auth, isValidToken, token);
    if (bearerToken == '') return;

    fetchStarlinkTerminalTopup(terminalId, customerId, bearerToken)
      .then((data) => {
        setTopup(data);
        setTopupLoading(false);
      })
      .catch(() => {
        setTopupLoading(false);
      });
  };

  useEffect(() => {
    if (terminal.terminal_id == terminalId) {
      getStarlinkTerminalUtilisation();
      getStarlinkTerminalTelemetry();
      getStarlinkTerminalTopup();
    }
  }, [terminal]);

  return (
    <>
      <Row>
        <Col>
          <h1 className='text-center'>{terminalId}</h1>
        </Col>
      </Row>

      {terminalLoading ? (
        <>
          {errors.length == 0 ? (
            <Alert variant='warning'>
              <Spinner animation='border' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </Spinner>
              <span>Loading Data...</span>
            </Alert>
          ) : (
            <Alerts alerts={errors} />
          )}
        </>
      ) : (
        <>
          {!terminal.service_line && (
            <Alert variant='warning'>
              <Alert.Heading>Inactive subscription</Alert.Heading>
              This terminal does not have a subscription attached.
            </Alert>
          )}
          <Row className='mb-3'>
            <Col sm={6}>
              <Card>
                <Card.Header>Terminal</Card.Header>
                <Card.Body>
                  <Table striped>
                    <tbody>
                      <tr>
                        <th>Terminal ID</th>
                        <td>{terminal.terminal_id}</td>
                      </tr>
                      <tr>
                        <th>Kit S/N</th>
                        <td>{terminal.kit_serial}</td>
                      </tr>
                      <tr>
                        <th>Dish S/N</th>
                        <td>{terminal.dish_serial}</td>
                      </tr>
                      {terminal.site && (
                        <tr>
                          <th>Location</th>
                          <td>{terminal.site}</td>
                        </tr>
                      )}
                      {terminal.service_line && (
                        <tr
                          className={
                            terminal.service_line.public_ip ? 'table-success' : 'table-warning'
                          }
                        >
                          <th>Public IP</th>
                          <td>{terminal.service_line.public_ip ? 'YES' : 'NO'}</td>
                        </tr>
                      )}
                      {terminal.subnets.v4 && (
                        <tr>
                          <th>IP</th>
                          <td>
                            <span>{terminal.subnets.v4}</span>
                          </td>
                        </tr>
                      )}
                      {telemetry?.terminal?.uptime && (
                        <tr>
                          <th>Uptime</th>
                          <td>
                            <Moment
                              durationFromNow
                              trim
                              subtract={{
                                seconds: telemetry.terminal.uptime.latest,
                              }}
                            />
                          </td>
                        </tr>
                      )}
                      {terminal.service_line && (
                        <>
                          <tr>
                            <th>Alias</th>
                            <td>{terminal.service_line.nickname}</td>
                          </tr>
                          <tr>
                            <th>Address</th>
                            <td>{terminal.service_line.address?.formattedAddress}</td>
                          </tr>
                          <tr
                            className={
                              terminal.service_line.active ? 'table-success' : 'table-warning'
                            }
                          >
                            <th>Service</th>
                            <td>{terminal.service_line.active ? 'ACTIVE' : 'SUSPENDED'}</td>
                          </tr>
                        </>
                      )}
                      {terminal.requested_plan && (
                        <tr>
                          <th>Service Plan</th>
                          <td>{terminal.requested_plan}</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>

                  <hr />
                  <h4>Routers</h4>

                  <Table striped>
                    <thead>
                      <tr>
                        <th>Router ID</th>
                        <th>&nbsp;</th>
                        <th>Uptime</th>
                      </tr>
                    </thead>
                    <tbody>
                      {terminal.routers.map((router) => (
                        <tr key={router.router_id}>
                          <td>{router.router_id}</td>
                          <td>{router.nickname == null ? '' : router.nickname}</td>
                          {telemetry?.routers && (
                            <>
                              {telemetry.routers['Router-' + router.router_id]?.uptime && (
                                <td>
                                  <Moment
                                    durationFromNow
                                    trim
                                    subtract={{
                                      seconds:
                                        telemetry.routers['Router-' + router.router_id].uptime
                                          .latest,
                                    }}
                                  />
                                </td>
                              )}
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
            {terminal.service_line &&
              (utilisationLoading ? (
                <>
                  {errors.length == 0 ? (
                    <Alert variant='warning'>
                      <Spinner animation='border' role='status'>
                        <span className='visually-hidden'>Loading...</span>
                      </Spinner>
                      <span>Loading Data...</span>
                    </Alert>
                  ) : (
                    <Alerts alerts={errors} />
                  )}
                </>
              ) : (
                <Col sm={6}>
                  <Card>
                    <Card.Header>Utilisation</Card.Header>
                    <Card.Body>
                      <Chart
                        type='pie'
                        options={utilisation.options}
                        series={utilisation.series}
                        height='300px'
                      />

                      {utilisation.overContractGB > 0 && (
                        <Alert variant='danger'>
                          Over contract by {formatGB(utilisation.overContractGB)}
                        </Alert>
                      )}

                      {utilisation.planExhausted && <Alert variant='warning'>Plan exhausted</Alert>}

                      {!topupLoading &&
                        topup &&
                        [...(topup.optin_blocks || []), ...(topup.onetime_blocks || [])].length >
                          0 && (
                          <>
                            <hr />
                            <h4>Top-up Blocks</h4>
                            <Table striped>
                              <thead>
                                <tr>
                                  <th>Type</th>
                                  <th>Amount</th>
                                  <th>Count</th>
                                  <th>Expires</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(topup.optin_blocks || []).map((b, i) => (
                                  <tr key={'optin-' + i}>
                                    <td>Opt-in</td>
                                    <td>
                                      {b.data_amount} {b.data_unit}
                                    </td>
                                    <td>{b.count}</td>
                                    <td>
                                      <Moment format='D MMM YYYY'>{b.expiration_date}</Moment>
                                    </td>
                                  </tr>
                                ))}
                                {(topup.onetime_blocks || []).map((b, i) => (
                                  <tr key={'onetime-' + i}>
                                    <td>One-time</td>
                                    <td>
                                      {b.data_amount} {b.data_unit}
                                    </td>
                                    <td>{b.count}</td>
                                    <td>
                                      <Moment format='D MMM YYYY'>{b.expiration_date}</Moment>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </>
                        )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
          </Row>
        </>
      )}

      {!terminalLoading &&
        terminal.service_line &&
        (utilisationLoading ? (
          <>
            {errors.length == 0 ? (
              <Alert variant='warning'>
                <Spinner animation='border' role='status'>
                  <span className='visually-hidden'>Loading...</span>
                </Spinner>
                <span>Loading Data...</span>
              </Alert>
            ) : (
              <Alerts alerts={errors} />
            )}
          </>
        ) : (
          <Row className='mb-3'>
            <Col>
              <Card>
                <Card.Header>Utilisation</Card.Header>
                <Card.Body>
                  <Row>
                    <Col>
                      <Tabs id='utilisation-tabs' defaultActiveKey='weekly' className='mb-3'>
                        <Tab eventKey='weekly' title='Weekly'>
                          <div className='d-flex justify-content-center align-items-center gap-3 my-2'>
                            <Button
                              variant='outline-secondary'
                              size='sm'
                              onClick={() => navigateHistory('weekly', offsets.weekly + 1)}
                              disabled={historyNavLoading.weekly || !hasEarlier.weekly}
                            >
                              ◀
                            </Button>
                            {historyNavLoading.weekly ? (
                              <Spinner animation='border' size='sm' role='status' />
                            ) : (
                              <span>{periodLabel('weekly', offsets.weekly, timezone)}</span>
                            )}
                            <Button
                              variant='outline-secondary'
                              size='sm'
                              onClick={() => navigateHistory('weekly', offsets.weekly - 1)}
                              disabled={historyNavLoading.weekly || offsets.weekly === 0}
                            >
                              ▶
                            </Button>
                          </div>
                          {history['weekly']?.isEmpty ? (
                            <Alert variant='info' className='mt-3'>
                              No data consumed in this period.
                            </Alert>
                          ) : (
                            <Chart
                              type='bar'
                              options={history['weekly'].options}
                              series={history['weekly'].series}
                              height='300px'
                            />
                          )}
                        </Tab>
                        <Tab eventKey='monthly' title='Monthly'>
                          <div className='d-flex justify-content-center align-items-center gap-3 my-2'>
                            <Button
                              variant='outline-secondary'
                              size='sm'
                              onClick={() => navigateHistory('monthly', offsets.monthly + 1)}
                              disabled={historyNavLoading.monthly || !hasEarlier.monthly}
                            >
                              ◀
                            </Button>
                            {historyNavLoading.monthly ? (
                              <Spinner animation='border' size='sm' role='status' />
                            ) : (
                              <span>{periodLabel('monthly', offsets.monthly, timezone)}</span>
                            )}
                            <Button
                              variant='outline-secondary'
                              size='sm'
                              onClick={() => navigateHistory('monthly', offsets.monthly - 1)}
                              disabled={historyNavLoading.monthly || offsets.monthly === 0}
                            >
                              ▶
                            </Button>
                          </div>
                          {history['monthly']?.isEmpty ? (
                            <Alert variant='info' className='mt-3'>
                              No data consumed in this period.
                            </Alert>
                          ) : (
                            <Chart
                              type='bar'
                              options={history['monthly'].options}
                              series={history['monthly'].series}
                              height='300px'
                            />
                          )}
                        </Tab>
                        <Tab eventKey='yearly' title='Yearly'>
                          <div className='d-flex justify-content-center align-items-center gap-3 my-2'>
                            <Button
                              variant='outline-secondary'
                              size='sm'
                              onClick={() => navigateHistory('yearly', offsets.yearly + 1)}
                              disabled={historyNavLoading.yearly || !hasEarlier.yearly}
                            >
                              ◀
                            </Button>
                            {historyNavLoading.yearly ? (
                              <Spinner animation='border' size='sm' role='status' />
                            ) : (
                              <span>{periodLabel('yearly', offsets.yearly, timezone)}</span>
                            )}
                            <Button
                              variant='outline-secondary'
                              size='sm'
                              onClick={() => navigateHistory('yearly', offsets.yearly - 1)}
                              disabled={historyNavLoading.yearly || offsets.yearly === 0}
                            >
                              ▶
                            </Button>
                          </div>
                          <Form.Check // prettier-ignore
                            type='switch'
                            id='yearly-resolution-switch'
                            label={yearlyResolution === 'monthly' ? 'Monthly' : 'Weekly'}
                            checked={yearlyResolution === 'monthly'}
                            onChange={(e) =>
                              setYearlyResolution(e.target.checked ? 'monthly' : 'weekly')
                            }
                            className='mb-2'
                          />
                          {history[`yearly_${yearlyResolution}`]?.isEmpty ? (
                            <Alert variant='info' className='mt-3'>
                              No data consumed in this period.
                            </Alert>
                          ) : (
                            <Chart
                              type='bar'
                              options={history[`yearly_${yearlyResolution}`].options}
                              series={history[`yearly_${yearlyResolution}`].series}
                              height='300px'
                            />
                          )}
                        </Tab>
                      </Tabs>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ))}

      {!terminalLoading && terminal.service_line && (
        <Row className='mb-3'>
          <Col>
            <Accordion as={Card} flush defaultActiveKey='telemetry'>
              <Accordion.Item eventKey='telemetry'>
                <Accordion.Header>Telemetry</Accordion.Header>
                <Accordion.Body>
                  {telemetryLoading ? (
                    <>
                      {errors.length == 0 ? (
                        <Alert variant='warning'>
                          <Spinner animation='border' role='status'>
                            <span className='visually-hidden'>Loading...</span>
                          </Spinner>
                          <span>Loading Data...</span>
                        </Alert>
                      ) : (
                        <Alerts alerts={errors} />
                      )}
                    </>
                  ) : (
                    <Tabs id='telemetry-tabs' defaultActiveKey='daily' className='mb-3'>
                      <Tab eventKey='daily' title='Last 1 Day'>
                        <StarlinkTelemetry telemetry={telemetry} resolution='daily' />
                      </Tab>
                      <Tab eventKey='weekly' title='Last 1 Week'>
                        <StarlinkTelemetry telemetry={telemetry} resolution='weekly' />
                      </Tab>
                      <Tab eventKey='monthly' title='Last 1 Month'>
                        <StarlinkTelemetry telemetry={telemetry} resolution='monthly' />
                      </Tab>
                      <Tab eventKey='yearly' title='Last 1 Year'>
                        <StarlinkTelemetry telemetry={telemetry} resolution='yearly' />
                      </Tab>
                    </Tabs>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Col>
        </Row>
      )}
    </>
  );
}
